import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import pick from 'lodash/pick';
import get from 'lodash/get';

import { completeNewPassword } from '../Authorization/actions';
import { translateError } from '../../cognito/cognito-service';
import Container from '../../components/Container';
import Segment from '../../components/Segment';
import Dimmer from '../../components/Dimmer';
import Loader from '../../components/Loader';
import Header from '../../components/Header';
import Form from '../../components/Form';
import Input from '../../components/Input';
import Icon from '../../components/Icon';
import Button from '../../components/Button';
import Label from '../../components/Label';
import Message from '../../components/Message';
import PasswordRules from '../../components/PasswordRules';

const messages = defineMessages({
  username: {
    id: 'new-password.usernamePlaceholder',
    defaultMessage: 'E-mail Address',
  },
  password: {
    id: 'new-password.passwordPlaceholder',
    defaultMessage: 'New Password',
  },
  confirmPassword: {
    id: 'new-password.confirmPasswordPlaceholder',
    defaultMessage: 'Confirm Password',
  },
  InvalidParameterException: {
    id: 'new-password.invalidParameterException',
    defaultMessage: 'Password failed to satisfy security requirements.',
  },
  mismatchedPassword: {
    id: 'new-password.error.mismatchedPassword',
    defaultMessage: 'Passwords must match',
  },
});

function verifyForm(formData) {
  const errors = {
    passwordErrorLabel: null,
    confirmPassword: null,
    errorCode: null,
  };
  if (!formData.password) {
    errors.passwordErrorLabel = (
      <Label role="tooltip" basic color="red" pointing>
        <FormattedMessage id="new-password.error.missingPassword" defaultMessage="Please enter a password" />
      </Label>
    );
  }
  if (!formData.confirmPassword) {
    errors.confirmPasswordErrorLabel = (
      <Label role="tooltip" basic color="red" pointing>
        <FormattedMessage id="new-password.error.missingConfirmPassword" defaultMessage="Please enter a confirmation password" />
      </Label>
    );
  }
  if (formData.password !== formData.confirmPassword) {
    errors.errorCode = 'mismatchedPassword';
  }
  return errors;
}

class NewPassword extends React.PureComponent {
  static defaultState = {
    verificationErrorLabel: null,
    passwordErrorLabel: null,
    confirmPasswordErrorLabel: null,
    errorCode: null,
    requesting: false,
  };

  static propTypes = {
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    completeNewPassword: PropTypes.func.isRequired,
    history: PropTypes.shape({
      replace: PropTypes.func.isRequired,
    }).isRequired,
    current: PropTypes.shape({
      viewer: PropTypes.shape({
        cognitoUser: PropTypes.shape({
          username: PropTypes.string,
          Session: PropTypes.string,
        }),
      }).isRequired,
    }).isRequired,
  };

  constructor(props) {
    super(props);
    this.state = NewPassword.defaultState;
  }

  componentDidMount() {
    const { Session, username } = get(this.props, 'current.viewer.cognitoUser', {});
    if (!Session || !username) {
      this.props.history.replace('/login');
    }
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps({ current }) {
    const errorCode = get(current, 'viewer.errorCode');
    if (errorCode !== get(this.props, 'current.viewer.errorCode')) {
      this.setState({
        errorCode,
        requesting: false,
      });
    }
  }

  _onChange = (e, { value, name } = {}) => {
    value = value || e.target.value;
    name = name || e.target.name;
    this.setState({ [name]: value });
  };

  _onSubmit = (e) => {
    const formData = pick(this.state, 'password', 'confirmPassword');
    e.preventDefault();
    const errors = verifyForm(formData);
    if (Object.keys(errors).some((key) => errors[key])) {
      this.setState(errors);
    } else {
      errors.requesting = true;
      this.setState(errors);
      this.props.completeNewPassword(Object.assign(pick(formData, 'password'), get(this.props, 'current.viewer.cognitoUser')));
    }
  };

  render() {
    const { intl, current } = this.props;
    const { passwordErrorLabel, confirmPasswordErrorLabel, errorCode, requesting, password, confirmPassword } = this.state;
    const errorMessage = errorCode ? (
      <Message role="alert" negative>
        <Message.Header><FormattedMessage {...translateError(errorCode)} {...messages[errorCode]} /></Message.Header>
      </Message>
    ) : '';
    // can't beat the auto complete, but I'm sure going to try
    // https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion#Disabling_autocompletion
    return (
      <Container text className="loginPanel">
        <Header as="h1"><FormattedMessage id="new-password.header" defaultMessage="New Password Needed" /></Header>
        <Dimmer.Dimmable as={Form} onSubmit={this._onSubmit} size="large" autoComplete="nope">
          <Dimmer tabIndex={-1} role="progressbar" aria-describedby="Logging In..." aria-busy={requesting} inverted active={requesting}>
            <Loader active={requesting} />
          </Dimmer>
          <Segment>
            <Form.Field>
              <label htmlFor="current-username-input">
                <FormattedMessage {...messages.username} />
              </label>
              <Input type="email" name="username" value={get(current, 'viewer.cognitoUser.email')} icon iconPosition="left">
                <Icon name="mail" />
                <input
                  id="current-username-input"
                  disabled
                  name="username"
                  aria-disabled
                  aria-label={intl.formatMessage(messages.username)}
                  autoComplete="nope"
                />
              </Input>
            </Form.Field>
            <Form.Field>
              <label htmlFor="new-password-input">
                <FormattedMessage {...messages.password} />
              </label>
              <Input name="password" value={password} icon iconPosition="left" onChange={this._onChange}>
                <Icon name="lock" />
                <input
                  type="password"
                  id="new-password-input"
                  aria-label={intl.formatMessage(messages.password)}
                  autoComplete="new-password"
                  required
                  aria-invalid={!!passwordErrorLabel}
                  aria-required
                />
              </Input>
              { passwordErrorLabel }
              <PasswordRules />
            </Form.Field>
            <Form.Field>
              <label htmlFor="confirm-password-input">
                <FormattedMessage {...messages.confirmPassword} />
              </label>
              <Input name="confirmPassword" value={confirmPassword} icon iconPosition="left" onChange={this._onChange}>
                <Icon name="lock" />
                <input
                  type="password"
                  id="confirm-password-input"
                  aria-label={intl.formatMessage(messages.confirmPassword)}
                  aria-invalid={!!confirmPasswordErrorLabel}
                  required
                  aria-required
                />
              </Input>
              { confirmPasswordErrorLabel }
            </Form.Field>
            {errorMessage}
            <Button primary type="submit" fluid>
              <FormattedMessage id="new-password.changePasswordButton" defaultMessage="Change Password" />
            </Button>
          </Segment>
        </Dimmer.Dimmable>
      </Container>
    );
  }
}

const mapStateToProps = (state) => ({
  current: state.current,
});

export const NewPasswordComponent = injectIntl(withRouter(NewPassword));
export default connect(mapStateToProps, { completeNewPassword })(NewPasswordComponent);
